import React, { useEffect, useRef, useState } from 'react';

interface AudioPlayerProps {
  src: string;
  /** 最大宽度，默认 320px */
  maxWidth?: number | string;
}

function formatTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * 轻量、现代化的音频播放器：圆形播放/暂停按钮 + 可拖动进度条 + 时间显示。
 * 用 CSS 变量（带回退）适配主题，替代原生难看的 <audio controls>。
 */
export default function AudioPlayer({ src, maxWidth = 320 }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [hover, setHover] = useState(false);

  // 切换音源时复位
  useEffect(() => {
    setPlaying(false);
    setCurrent(0);
    setDuration(0);
  }, [src]);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) el.pause();
    else void el.play();
  };

  const seekTo = (clientX: number) => {
    const track = trackRef.current;
    const el = audioRef.current;
    if (!track || !el || !Number.isFinite(duration) || duration <= 0) return;
    const rect = track.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const t = ratio * duration;
    el.currentTime = t;
    setCurrent(t);
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragging(true);
    seekTo(e.clientX);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragging) seekTo(e.clientX);
  };
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    setDragging(false);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const pct = duration > 0 ? Math.min(100, (current / duration) * 100) : 0;

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.6rem',
        width: '100%',
        maxWidth,
        padding: '0.35rem 0.65rem',
        borderRadius: '999px',
        background: 'var(--surface-2, #f1f3f5)',
        border: '1px solid var(--border, #e5e7eb)',
        boxSizing: 'border-box',
      }}
    >
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setCurrent(0); }}
        onTimeUpdate={e => setCurrent(e.currentTarget.currentTime)}
        onLoadedMetadata={e => setDuration(e.currentTarget.duration)}
      />

      <button
        type="button"
        onClick={toggle}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        aria-label={playing ? 'Pause' : 'Play'}
        style={{
          flexShrink: 0,
          width: '30px',
          height: '30px',
          borderRadius: '50%',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--primary, #6366f1)',
          color: '#fff',
          transition: 'transform 0.12s ease, filter 0.12s ease',
          transform: hover ? 'scale(1.06)' : 'scale(1)',
          filter: hover ? 'brightness(1.06)' : 'none',
        }}
      >
        {playing ? (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
            <rect x="2.2" y="1.5" width="2.6" height="9" rx="0.9" />
            <rect x="7.2" y="1.5" width="2.6" height="9" rx="0.9" />
          </svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
            <path d="M3.2 2.1c0-.62.68-1 1.2-.66l5.2 3.4a.8.8 0 0 1 0 1.32l-5.2 3.4a.79.79 0 0 1-1.2-.66V2.1Z" />
          </svg>
        )}
      </button>

      <div
        ref={trackRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        style={{
          position: 'relative',
          flex: 1,
          height: '6px',
          borderRadius: '999px',
          background: 'var(--border, #d1d5db)',
          cursor: 'pointer',
          touchAction: 'none',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            width: `${pct}%`,
            borderRadius: '999px',
            background: 'var(--primary, #6366f1)',
            transition: dragging ? 'none' : 'width 0.1s linear',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: `${pct}%`,
            width: '11px',
            height: '11px',
            borderRadius: '50%',
            background: '#fff',
            border: '2px solid var(--primary, #6366f1)',
            transform: 'translate(-50%, -50%)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
            transition: dragging ? 'none' : 'left 0.1s linear',
          }}
        />
      </div>

      <span
        style={{
          flexShrink: 0,
          fontSize: '0.72rem',
          color: 'var(--text-2, #6b7280)',
          fontVariantNumeric: 'tabular-nums',
          minWidth: '70px',
          textAlign: 'right',
        }}
      >
        {formatTime(current)} / {formatTime(duration)}
      </span>
    </div>
  );
}
